// Il dettaglio di un progetto, scheda «Panoramica» (spec «Struttura della plancia», S3): il suo
// piano (requisiti, team, fasi), le attività di Pianifica che lo fanno lavorare, le sessioni e i
// consumi per giorno. Le impostazioni (git, commit, push) stanno nell'altra scheda.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { RelativeTime } from '../../components/RelativeTime';
import { StatusPill } from '../../components/StatusPill';
import { ApiError, api } from '../../lib/api';
import type { AgentProfile, GitRegistry, Project, Routine, Usage } from '../../lib/api';
import { tokens as fmtTokens } from '../../lib/format';
import { useStore } from '../../store/StoreProvider';
import { Bars } from '../control/widgets/Bars';
import { Lane } from '../runtime/ActivityRuntime';
import { EMPTY_PLAN } from './new/plan';
import { StepSummary } from './new/StepSummary';

const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function ProjectOverview({
  project,
  registries,
  onSettings,
}: {
  project: Project;
  registries: GitRegistry[];
  onSettings: () => void;
}) {
  const { state, toast, reload, dispatch } = useStore();
  const nav = useNavigate();
  const [routines, setRoutines] = useState<Routine[] | null>(null);
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  useEffect(() => {
    void api.routines().then(
      (r) => setRoutines(r.filter((x) => x.project === project.id)),
      () => setRoutines([]),
    );
    void api.profiles().then(setProfiles, () => setProfiles([]));
    void api.usage(30, ZONE).then(setUsage, () => setUsage(null));
  }, [project.id]);

  const sessions = state.sessions
    .filter((s) => s.project === project.id)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const live = sessions.filter((s) => !['completed', 'failed', 'cancelled'].includes(s.state));
  const liveIds = live.map((s) => s.id).join(',');
  // i lavori del piano delle sessioni vive, per la corsia dal vivo; poi li aggiorna lo stream
  useEffect(() => {
    for (const id of liveIds.split(',').filter(Boolean))
      void api.tasks(id).then((tasks) => dispatch({ type: 'tasks/set', sessionId: id, tasks }));
  }, [liveIds, dispatch]);
  const series = usage?.byProject[project.id]?.tokens ?? usage?.days.map(() => 0) ?? [];
  const total = series.reduce((a, b) => a + b, 0);

  const start = async (r: Routine) => {
    setStarting(r.id);
    try {
      const s = await api.runRoutine(r.id);
      reload();
      nav('/s/' + s.id);
    } catch (e) {
      const body = e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.';
      toast({ kind: 'danger', title: 'Non è partita', body });
      setStarting(null);
    }
  };

  const archive = async () => {
    try {
      await api.archiveProject(project.id, true);
      toast({
        kind: 'success',
        title: `${project.name}: in Archivio`,
        body: 'Si ripristina da lì.',
      });
      nav('/progetti');
    } catch {
      toast({ kind: 'danger', title: 'Non archiviato', body: 'Riprova fra poco.' });
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="sm" icon="box" onClick={() => void archive()}>
          Archivia il progetto
        </Button>
      </div>
      <div className="np-two" style={{ alignItems: 'stretch' }}>
        <section className="np-card">
          <header className="np-card-head">
            <h2 className="t-overline">Attività del progetto</h2>
            <Link to="/pianifica" className="dash-more" style={{ marginLeft: 'auto' }}>
              Pianifica
            </Link>
          </header>
          {routines === null ? (
            <div className="t-caption faint">Leggo le attività…</div>
          ) : routines.length === 0 ? (
            <div className="t-caption faint">Nessuna attività legata a questo progetto.</div>
          ) : (
            routines.map((r) => (
              <div key={r.id} className="np-row">
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="t-strong" style={{ display: 'block' }}>
                    {r.name}
                  </span>
                  <span className="t-caption dim">
                    {r.lastRun ? (
                      <>
                        ultima <RelativeTime at={r.lastRun.at} />
                      </>
                    ) : (
                      'mai avviata'
                    )}
                  </span>
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  icon="play"
                  loading={starting === r.id}
                  disabled={!!starting}
                  onClick={() => void start(r)}
                >
                  Avvia
                </Button>
              </div>
            ))
          )}
        </section>
        <section className="np-card">
          <header className="np-card-head">
            <h2 className="t-overline">Consumi · 30 giorni</h2>
            <span className="t-caption faint" style={{ marginLeft: 'auto' }}>
              {fmtTokens(total)} token
            </span>
          </header>
          <Bars
            values={series}
            days={usage?.days ?? []}
            height={110}
            format={(v) => `${fmtTokens(v)} token`}
          />
        </section>
      </div>
      {live.length > 0 && (
        <section style={{ display: 'grid', gap: 10 }} aria-label="Lavorazione dal vivo">
          <h2 className="t-overline dim" style={{ margin: 0 }}>
            Lavorazione dal vivo
          </h2>
          {live.map((s) => (
            <Lane key={s.id} session={s} tasks={state.tasks[s.id] ?? []} trace={state.trace} />
          ))}
        </section>
      )}
      <section className="np-card">
        <header className="np-card-head">
          <h2 className="t-overline">Sessioni</h2>
          <span className="t-caption faint">{sessions.length}</span>
        </header>
        {sessions.length === 0 ? (
          <div className="t-caption faint">Ancora nessuna sessione.</div>
        ) : (
          sessions.slice(0, 10).map((s) => (
            <Link key={s.id} to={`/s/${s.id}`} className="agent-card dash-row">
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-strong trunc" style={{ display: 'block' }}>
                  {s.title}
                </span>
                <span className="t-caption dim">
                  <RelativeTime at={s.updatedAt} />
                </span>
              </span>
              <StatusPill state={s.state} size="sm" />
            </Link>
          ))
        )}
      </section>
      <StepSummary
        form={project}
        plan={project.plan ?? EMPTY_PLAN}
        registries={registries}
        profiles={profiles}
        onEdit={onSettings}
      />
    </div>
  );
}
